import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { 
    config: String,
    content: String 
  }

  connect() {
    // Add a small delay to ensure TinyMCE assets are loaded
    setTimeout(() => this.initializeTinyMCE(), 100)
    
    // Listen for theme changes
    this.themeObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          console.log('Theme changed, reinitializing TinyMCE...')
          this.reinitializeWithTheme()
        }
      })
    })
    
    this.themeObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-theme']
    })
  }

  disconnect() {
    this.destroyTinyMCE()
    if (this.themeObserver) {
      this.themeObserver.disconnect()
    }
  }

  reinitializeWithTheme() {
    if (window.tinymce && tinymce.get(this.element.id)) {
      // Save current content
      const currentContent = tinymce.get(this.element.id).getContent()
      
      // Destroy and recreate with new theme
      this.destroyTinyMCE()
      
      // Update the config with current theme
      this.updateConfigForCurrentTheme()
      
      // Reinitialize
      setTimeout(() => {
        this.initializeTinyMCE()
        // Restore content after initialization
        setTimeout(() => {
          const editor = tinymce.get(this.element.id)
          if (editor && currentContent) {
            editor.setContent(currentContent)
          }
        }, 100)
      }, 100)
    }
  }

  updateConfigForCurrentTheme() {
    const isDark = document.body.getAttribute('data-theme') === 'dark'
    
    if (window.tinyMCEConfig && window.tinyMCEConfig.default) {
      // Update skin
      window.tinyMCEConfig.default.skin = isDark ? 'oxide-dark' : 'oxide'
      
      // Update content styles with more comprehensive styling
      // IMPORTANT: Don't use !important on table cell styles to allow user customization
      window.tinyMCEConfig.default.content_style = `
        body { 
          font-family: Arial, sans-serif !important;
          font-size: 16px !important;
          line-height: 1.5 !important;
          background-color: ${isDark ? '#22252a' : '#ffffff'} !important;
          color: ${isDark ? '#c9cacb' : '#000000'} !important;
          margin: 8px !important;
          padding: 8px !important;
        }
        p, div, span, h1, h2, h3, h4, h5, h6 {
          color: ${isDark ? '#c9cacb' : '#000000'};
        }
        table { 
          border-collapse: collapse; 
          width: 100%; 
        }
        table td, table th { 
          border: 1px solid ${isDark ? '#495057' : '#ddd'}; 
          padding: 8px; 
          /* Don't override user-applied colors and background colors */
        }
        table td p, table th p {
          margin: 0 !important;
          padding: 0 !important;
        }
        table th { 
          background-color: ${isDark ? '#353b3f' : '#f2f2f2'}; 
          font-weight: bold;
        }
        /* Only apply default colors when no inline styles are present */
        table td:not([style*="color"]), table th:not([style*="color"]) {
          color: ${isDark ? '#c9cacb' : '#000000'};
        }
        table td:not([style*="background"]), table th:not([style*="background"]) {
          background-color: ${isDark ? '#22252a' : '#ffffff'};
        }
        a {
          color: ${isDark ? '#80bdff' : '#0d6efd'} !important;
        }
      `
    }
  }

  initializeTinyMCE() {
    // Remove any existing TinyMCE instance on this element
    if (window.tinymce && tinymce.get(this.element.id)) {
      tinymce.remove(`#${this.element.id}`)
    }

    // Ensure element has an ID for TinyMCE
    if (!this.element.id) {
      this.element.id = `tinymce-${Math.random().toString(36).substr(2, 9)}`
    }

    // Wait for TinyMCE to load if not already loaded
    if (typeof window.tinymce === 'undefined') {
      console.log('TinyMCE not loaded yet, retrying...')
      setTimeout(() => this.initializeTinyMCE(), 500)
      return
    }

    // Update config for current theme
    this.updateConfigForCurrentTheme()

    // Get configuration (default to 'default' if not specified)
    const configName = this.hasConfigValue ? this.configValue : 'default'
    const baseConfig = window.tinyMCEConfig ? window.tinyMCEConfig[configName] : {}
    
    console.log('Initializing TinyMCE with config:', baseConfig)
    
    // Initialize TinyMCE with enhanced table configuration
    tinymce.init({
      selector: `#${this.element.id}`,
      license_key: 'gpl',
      ...baseConfig,
      
      // Explicitly ensure table plugin is loaded
      plugins: baseConfig.plugins || 'lists link charmap preview anchor searchreplace visualblocks code fullscreen insertdatetime media table code help wordcount codesample emoticons autoresize',
            
      // ActionText compatibility
      setup: (editor) => {
        console.log('TinyMCE editor setup for:', this.element.id)
        
        // Sync content back to the textarea for form submission
        editor.on('change', () => {
          this.element.value = editor.getContent()
        })
        
        // Handle form submission
        editor.on('submit', () => {
          this.element.value = editor.getContent()
        })

        // Log when editor is ready and apply theme
        editor.on('init', () => {
          console.log('TinyMCE editor initialized with plugins:', editor.plugins)
          
          // Apply theme styling to the iframe body
          const isDark = document.body.getAttribute('data-theme') === 'dark'
          const iframeDoc = editor.getDoc()
          
          if (iframeDoc && iframeDoc.body) {
            iframeDoc.body.style.backgroundColor = isDark ? '#22252a' : '#ffffff'
            iframeDoc.body.style.color = isDark ? '#c9cacb' : '#000000'
            
            // Add a style tag to the iframe for comprehensive styling that doesn't override inline styles
            const style = iframeDoc.createElement('style')
            style.textContent = `
              body {
                background-color: ${isDark ? '#22252a' : '#ffffff'} !important;
                color: ${isDark ? '#c9cacb' : '#000000'} !important;
              }
              p, div, span, h1, h2, h3, h4, h5, h6 {
                color: ${isDark ? '#c9cacb' : '#000000'};
              }
              /* Don't override inline styles on table elements */
              table td:not([style*="color"]), table th:not([style*="color"]) {
                color: ${isDark ? '#c9cacb' : '#000000'};
              }
              table td:not([style*="background"]), table th:not([style*="background"]) {
                background-color: ${isDark ? '#22252a' : '#ffffff'};
              }
            `
            iframeDoc.head.appendChild(style)
          }
        })
      },

      // Ensure content is properly formatted
      forced_root_block: 'p',
      force_br_newlines: false,
      force_p_newlines: false,
      
      // Better content handling
      convert_urls: false,
      relative_urls: false,
      remove_script_host: false,
      
      // ActionText-friendly settings
      entity_encoding: 'raw',
      cleanup: false,
      verify_html: false
    })
  }

  destroyTinyMCE() {
    if (this.element.id && window.tinymce && tinymce.get(this.element.id)) {
      tinymce.remove(`#${this.element.id}`)
    }
  }

  // Method to manually sync content (useful for Turbo)
  syncContent() {
    if (window.tinymce) {
      const editor = tinymce.get(this.element.id)
      if (editor) {
        this.element.value = editor.getContent()
      }
    }
  }
}

// Ensure all TinyMCE editors are destroyed before Turbo caches the page
// This prevents stale editors and content leaks when navigating with Turbo
if (typeof window !== 'undefined') {
  document.addEventListener("turbo:before-cache", () => {
    if (window.tinymce) {
      window.tinymce.remove();
    }
  });
} 